import Axios, {CancelToken, Canceler} from 'axios';

export type AppraisalRequestKind = 'allowance' | 'appraisal' | 'history';

export interface AppraisalRequestContext {
  authToken: string;
  cancelToken: CancelToken;
  id: number;
  kind: AppraisalRequestKind;
}

interface ActiveAppraisalRequest extends AppraisalRequestContext {
  cancel: Canceler;
}

let nextRequestId = 0;
const activeRequests = new Map<number, ActiveAppraisalRequest>();
const latestRequestIds = new Map<AppraisalRequestKind, number>();

export const getCurrentAppraisalAuthToken = (): string =>
  localStorage.getItem('saw-token') || '';

const cancelRequestsByKind = (kind: AppraisalRequestKind): void => {
  activeRequests.forEach(request => {
    if (request.kind === kind) {
      request.cancel(`Superseded ${kind} request`);
    }
  });
};

export const beginAppraisalRequest = (
  kind: AppraisalRequestKind,
  cancelPrevious: boolean = false,
): AppraisalRequestContext => {
  if (cancelPrevious) cancelRequestsByKind(kind);

  const source = Axios.CancelToken.source();
  const request: ActiveAppraisalRequest = {
    authToken: getCurrentAppraisalAuthToken(),
    cancel: source.cancel,
    cancelToken: source.token,
    id: ++nextRequestId,
    kind,
  };
  activeRequests.set(request.id, request);
  latestRequestIds.set(kind, request.id);
  return request;
};

export const finishAppraisalRequest = (
  request: AppraisalRequestContext,
): void => {
  activeRequests.delete(request.id);
};

export const isAppraisalRequestSessionCurrent = (
  request: AppraisalRequestContext,
): boolean =>
  activeRequests.has(request.id) &&
  request.authToken === getCurrentAppraisalAuthToken();

export const isLatestAppraisalRequest = (
  request: AppraisalRequestContext,
): boolean =>
  isAppraisalRequestSessionCurrent(request) &&
  latestRequestIds.get(request.kind) === request.id;

export const cancelPendingAppraisalRequests = (): void => {
  activeRequests.forEach(request => {
    request.cancel('Authentication session changed');
  });
  activeRequests.clear();
  latestRequestIds.clear();
};
